import React, { ChangeEvent, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as END_POINT from "../../../../../config/config";
import { api, getToken } from "../../../../../services/apis/auth-api/apiUrl";
import * as props from "../../../../../utils/types";

interface filterDomainProps extends props.domainsProps {
    checked : boolean;
}

interface filterStatusProps extends props.statusProps {
    checked : boolean;
}

interface filterClientsProps extends props.clientsProps {
    checked : boolean;
}

interface roleFilterProps {
    onFilter : (filters : props.filterItemsProps) => void;
    filters : props.filterItemsProps;
}

interface clFilterProps {
    id : number;
    checked : boolean;
    cl : string;
}

const cl_items = Array.from({ length: 11 }, (_, i) => ({
    id : i + 1,
    checked: false,
    cl: `CL${15 - i}`
  })) as clFilterProps[];

const RoleFilter : React.FC<roleFilterProps> = ({onFilter, filters}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [domains, setDomains] = useState<filterDomainProps[]>([]);
    const [status, setStatus] = useState<filterStatusProps[]>([]);
    const [clients, setClients] = useState<filterClientsProps[]>([]);
    const [cl, setCl] = useState<clFilterProps[]>(cl_items);
    const [filterItems, setFilterItems] = useState<props.filterItemsProps>(filters);
    const [shiftSched, setShiftSched] = useState([
        {id : 1, shift : 'Day Shift', checked : filterItems.shift_shed.includes('Day Shift') ? true : false},
        {id : 2, shift : 'Night Shift', checked : filterItems.shift_shed.includes('Night Shift') ? true : false},
        {id : 3, shift : 'Shifting', checked : filterItems.shift_shed.includes('Shifting') ? true : false}
    ])

    //Retrieving domain data, client data and status from database
    useEffect(()=>{
        const getFilters = async() => {
            try{
                const domain_data = await api.get(END_POINT.GET_ALL_DOMAINS_END_POINT, {
                    headers: { 'Authorization': 'Bearer ' + getToken() }
                });

                const client_data = await api.get(END_POINT.GET_CLIENTS_END_POINT, {
                    headers: { 'Authorization': 'Bearer ' + getToken() }
                });

                const status_data = await api.get(END_POINT.GET_JDL_STATUS_END_POINT, {
                    headers: { 'Authorization': 'Bearer ' + getToken() }
                });

                const role_domains : filterDomainProps[] = domain_data.data;
                const role_clients : filterClientsProps[] = client_data.data;
                const role_status : filterStatusProps[] = status_data.data;

                setDomains(role_domains.map(domain => ({...domain, checked : filterItems.domains.includes(domain.id) ? true : false})));
                setClients(role_clients.map(client => ({...client, checked : filterItems.clients.includes(client.id) ? true : false})));
                setStatus(role_status.map(stat => ({...stat, checked : stat.status === 'Open' || filterItems.status.includes(stat.id) ? true : false})));
            }catch(e){
                console.error('Failed to load filters.');
            }finally{
                setLoading(false);
            }
        }
        getFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    //handling for selecting filter
    const handleSelect = (event : ChangeEvent<HTMLInputElement>, id : number) => {
        const isChecked = event.currentTarget.checked;
        const element = event.currentTarget.id;

        switch(element){
            case 'domain':
                const updatedDomainFilter = domains.map(domain =>(
                    domain.id === id ? {
                        ...domain, 
                        checked : isChecked
                    } : domain
                ))
                setDomains(updatedDomainFilter);
                break;
            case 'status':
                const updatedStatusFilter = status.map(stat => (
                    stat.id === id ? {
                        ...stat,
                        checked : isChecked
                    } : stat
                ));
                setStatus(updatedStatusFilter);
                break;
            case 'shift_sched':
                const updateShiftSched = shiftSched.map(shift => (
                    shift.id === id ? {
                        ...shift,
                        checked : isChecked
                    } : shift
                ));
                setShiftSched(updateShiftSched);
                break;
            case 'client':
                const updatedClient = clients.map(client => (
                    client.id === id ? {
                        ...client,
                        checked : isChecked
                    } : client
                ));
                setClients(updatedClient);
                break;
            case 'cl':
                const updateSelected = cl.map(item => (
                    item.id === id ? {
                        ...item, 
                        checked : isChecked
                    } : item
                ));

                setCl(updateSelected);
                
                break;
            default:
                break;
        }

    }

    //selecting all filters
    const handleSelectAll = (event : ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.currentTarget.checked;
        const element = event.currentTarget.id;

        switch(element){
            case 'domain':
                const updateAllDomain = domains.map(domain => ({...domain, checked : isChecked}));
                setDomains(updateAllDomain);
                break;
            case 'status':
                const updateAllStatus = status.map(stat => ({...stat, checked : isChecked}));
                setStatus(updateAllStatus);
                break;
            case 'shift_sched':
                const updateAllShiftSched = shiftSched.map(shift => ({...shift, checked : isChecked}));
                setShiftSched(updateAllShiftSched);
                break;
            case 'client':
                const updateAllClient = clients.map(client => ({...client, checked : isChecked}));
                setClients(updateAllClient);
                break;
            default:
                break;
        }
        
    }

    useEffect(()=>{
        const checkedDomains : Array<number>= [];
        const checkedShiftSched : Array<string> = [];
        const checkedClients : Array<number> = [];
        const checkedStat : Array<number> = [];
        const checkedCl : Array<string> = [];

        domains.map(domain => domain.checked ? checkedDomains.push(domain.id) : null);
        shiftSched.map(shift => shift.checked ? checkedShiftSched.push(shift.shift) : null);
        clients.map(client => client.checked ? checkedClients.push(client.id) : null);
        status.map(stat => stat.checked ? checkedStat.push(stat.id) : null);
        cl.map(item => item.checked ? checkedCl.push(item.cl) : null);

        setFilterItems({
            domains : checkedDomains, 
            shift_shed : checkedShiftSched,
            clients : checkedClients,
            status : checkedStat,
            cl : checkedCl
        })
    }, [clients, domains, shiftSched, status, cl]);

    return(
        <div className="min-w-96 max-w-[48rem] min-h-96 flex flex-col relative">
            <div className={loading ? 'modal-loader z-50' : 'hidden'}>
                <FontAwesomeIcon className="animate-spin" icon={faSpinner} />
            </div>
            <div className="my-2">
                <h1>Requirements Filter</h1>
            </div>
            <div className="flex gap-2">
                <div className="min-w-96">
                    <div>
                        <div>
                            <div className="border-b">
                                <h2 className="font-semibold text-gray-700">
                                    Domains
                                </h2>
                            </div>
                            <div className="my-2 text-gray-700">
                                <div className="flex gap-2 items-center">
                                    <div>
                                        <input type="checkbox" checked={domains.every(domain => domain.checked === true)} onChange={(e)=>handleSelectAll(e)} id="domain" className="check-box cursor-pointer" />
                                    </div>
                                    <div>Select All</div>
                                </div>
                                {domains && domains.map((domain)=>(
                                    <div className="flex gap-2 items-center">
                                        <div>
                                            <input type="checkbox" checked={domain.checked} onChange={(e)=>handleSelect(e, domain.id)} id="domain" className="check-box cursor-pointer" />
                                        </div>
                                        <div>{domain.domain_name}</div>
                                    </div>
                                ))}
                            </div>
                        </div> 
                    </div>
                    <div className="flex gap-4">
                    <div className="w-1/2">
                            <div className="border-b w-full">
                                <h2 className="font-semibold text-gray-700">Status</h2>
                            </div>
                            <div className="my-2">
                                <div className="flex gap-2 items-center">
                                    <div>
                                        <input type="checkbox" checked={status.every(stat => stat.checked === true)} onChange={(e)=>handleSelectAll(e)} id="status" className="check-box cursor-pointer" />
                                    </div>
                                    <div>Select All</div>
                                </div>
                                {status && status.map((stat)=>(
                                    <div className="flex gap-2 items-center">
                                        <div>
                                            <input type="checkbox" checked={stat.checked} onChange={(e)=>handleSelect(e, stat.id)} id="status" className="check-box cursor-pointer" />
                                        </div>
                                        <div>{stat.status}</div>
                                    </div>
                                ))}
                            </div>
                        </div>   
                        <div className="w-full">
                            <div className="border-b">
                                <h2 className="font-semibold text-gray-700">Shift Schedule</h2>
                            </div>
                            <div className="my-2">
                                <div className="flex gap-2 items-center">
                                    <div>
                                        <input type="checkbox" checked={shiftSched.every(shift => shift.checked === true)} onChange={(e)=>handleSelectAll(e)} id="shift_sched" className="check-box cursor-pointer" />
                                    </div>
                                    <div>Select All</div>
                                </div>
                                {shiftSched && shiftSched.map(shift => (
                                <div className="flex gap-2 items-center">
                                    <div>
                                        <input type="checkbox" checked={shift.checked} onChange={(e)=>handleSelect(e, shift.id)} id="shift_sched" className="check-box cursor-pointer" />
                                    </div>
                                    <div>{shift.shift}</div>
                                </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="min-h-52">
                        <div className="border-b w-full my-4">
                            <h2 className="font-semibold text-gray-700">Career Level</h2>
                        </div>
                        <div className="flex flex-wrap gap-8 h-full">
                                {cl.map(item => (
                                    <div className="flex gap-2 items-center">
                                        <div>
                                            <input type="checkbox" checked={item.checked} onChange={(e)=>handleSelect(e, item.id)} id="cl" className="check-box cursor-pointer" />
                                        </div>
                                        <div>{item.cl}</div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
                <div className="min-w-96">
                    <div className="border-b">
                        <h2 className="font-semibold text-gray-700">Clients</h2>
                    </div>
                    <div className="flex justify-end items-center mt-2 mb-1">
                        <div className="w-1/2">
                            <input type="text" className="text-box h-7" placeholder="Search....." />
                        </div>
                    </div>
                    <div className="table-container h-full">
                        <table className="w-full border">
                            <thead className="table-fixed-header border">
                                <tr className="bg-gray-100">
                                    <th className='table-headers font-medium w-36'>
                                        <div className="flex gap-2 items-center justify-center">
                                            <input type="checkbox" checked={clients.every(client => client.checked === true)} onChange={(e)=>handleSelectAll(e)} id="client" className="check-box cursor-pointer" />
                                            <h2>Select All</h2>
                                        </div>
                                    </th>
                                    <th className='table-headers font-medium'>Clients</th>
                                </tr>
                            </thead>
                            <tbody className="text-center">
                                {clients && clients.map((client, index)=>(
                                    <tr className="hover:bg-gray-300" key={index}>
                                        <td className='table-body px-2'>
                                            <input type="checkbox" checked={client.checked} onChange={(e)=>handleSelect(e, client.id)} id="client" className="check-box cursor-pointer" />
                                        </td>
                                        <td className='table-body px-2'>{client.client_name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="flex justify-end items-center mt-4">
                <div>
                    <button type="button" onClick={()=>onFilter(filterItems)} className="button button-vcc-primary">
                        <FontAwesomeIcon icon={faFilter} /> &nbsp;
                        Filter Requirements
                    </button>
                </div>
            </div>
        </div>
    );
}

export default RoleFilter;